import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitCareer } from "@/lib/api";
import {
  Briefcase,
  Users,
  HeartHandshake,
  GraduationCap,
  ShieldCheck,
  ArrowRight,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const COPY = {
  en: {
    eyebrow: "Careers at Orion FM",
    h1: "Join the team behind the UAE's most reliable facility operations.",
    intro:
      "From technicians and supervisors to engineers and account managers, Orion is hiring across Dubai, Abu Dhabi, Sharjah and Ajman. We sponsor visas, we invest in training and we promote from within.",
    perksTitle: "Why work at Orion",
    perks: [
      { Icon: ShieldCheck, t: "Visa sponsored by Orion", d: "Full visa & medical insurance under our license — no third-party agents." },
      { Icon: GraduationCap, t: "Continuous training", d: "Certified training in FM, HSE, cleaning chemistry and customer service." },
      { Icon: HeartHandshake, t: "Promote from within", d: "Our supervisors and managers come from our own ranks. Show up, deliver, grow." },
      { Icon: Users, t: "Real team culture", d: "Uniformed, ID-badged, respected. We take care of our people the way our clients expect us to take care of their sites." },
    ],
    rolesTitle: "Roles we frequently hire for",
    roles: [
      "FM Technician (HVAC / Plumbing / Electrical)",
      "Cleaning Supervisor",
      "Cleaning Crew (residential & commercial)",
      "Day Porter",
      "Pest Control Technician",
      "Facade & Rope Access Technician (IRATA)",
      "Site Supervisor",
      "Account Manager / Sales Executive",
      "Operations Coordinator",
      "Finance & Admin",
    ],
    formTitle: "Apply now",
    formNote: "We reply to every applicant. Suitable candidates are interviewed within 5 working days.",
    fields: {
      name: "Full Name *",
      email: "Email *",
      phone: "Phone *",
      role: "Role Applied For *",
      experience: "Years of Experience",
      nationality: "Nationality",
      visa: "Current Visa Status",
      cv: "Upload CV *",
      cvHint: "PDF or Word, max 5MB",
      cvLink: "CV Link (optional · Google Drive / LinkedIn)",
      cover: "Cover Message",
      submit: "Submit Application",
      sending: "Sending…",
    },
    successTitle: "Application received",
    successDesc: "Our HR team will get back to you within 5 working days.",
    error: "Something went wrong. Please try again or email careers@orionfm.ae directly.",
    validation: "Please fill in name, email, phone, role and upload your CV.",
    cvSizeError: "CV file is too large (max 5MB).",
    cvTypeError: "CV must be a PDF or Word document.",
  },
  ar: {
    eyebrow: "الوظائف في أوريون",
    h1: "انضم إلى الفريق الذي يدير أكثر منشآت الإمارات موثوقية.",
    intro:
      "من الفنيين والمشرفين إلى المهندسين ومديري الحسابات، نوظف في دبي وأبوظبي والشارقة وعجمان. نوفر كفالة التأشيرة، ونستثمر في التدريب، ونرقي من داخل الفريق.",
    perksTitle: "لماذا أوريون",
    perks: [
      { Icon: ShieldCheck, t: "كفالة أوريون", d: "تأشيرة وتأمين صحي شامل تحت رخصتنا — بدون وسطاء." },
      { Icon: GraduationCap, t: "تدريب مستمر", d: "تدريب معتمد في إدارة المرافق والسلامة وكيمياء التنظيف وخدمة العملاء." },
      { Icon: HeartHandshake, t: "الترقية من الداخل", d: "مشرفونا ومديرونا من داخل الفريق. التزم، أد، تنمو." },
      { Icon: Users, t: "ثقافة فريق حقيقية", d: "بزّة موحدة، بطاقة هوية، احترام. نهتم بفريقنا كما نهتم بمواقع عملائنا." },
    ],
    rolesTitle: "الوظائف التي نوظف لها بانتظام",
    roles: [
      "فني إدارة مرافق (تكييف / سباكة / كهرباء)",
      "مشرف نظافة",
      "طاقم نظافة (سكني وتجاري)",
      "مشرف يومي",
      "فني مكافحة آفات",
      "فني واجهات والوصول بالحبال (IRATA)",
      "مشرف موقع",
      "مدير حساب / تنفيذي مبيعات",
      "منسق عمليات",
      "إدارة ومالية",
    ],
    formTitle: "قدّم طلبك الآن",
    formNote: "نرد على كل متقدم. تتم مقابلة المرشحين المناسبين خلال 5 أيام عمل.",
    fields: {
      name: "الاسم الكامل *",
      email: "البريد الإلكتروني *",
      phone: "رقم الهاتف *",
      role: "الوظيفة المتقدم لها *",
      experience: "سنوات الخبرة",
      nationality: "الجنسية",
      visa: "حالة التأشيرة الحالية",
      cv: "ارفع السيرة الذاتية *",
      cvHint: "PDF أو Word، بحد أقصى 5 ميجابايت",
      cvLink: "رابط السيرة الذاتية (اختياري · Google Drive / LinkedIn)",
      cover: "رسالة تعريفية",
      submit: "إرسال الطلب",
      sending: "يتم الإرسال…",
    },
    successTitle: "تم استلام طلبك",
    successDesc: "سيتواصل فريق الموارد البشرية خلال 5 أيام عمل.",
    error: "حدث خطأ. الرجاء المحاولة مرة أخرى أو مراسلة careers@orionfm.ae مباشرة.",
    validation: "الرجاء تعبئة الاسم والبريد والهاتف والوظيفة ورفع السيرة الذاتية.",
    cvSizeError: "حجم ملف السيرة الذاتية كبير جداً (الحد 5 ميجابايت).",
    cvTypeError: "يجب أن تكون السيرة الذاتية بصيغة PDF أو Word.",
  },
};

const ROLE_OPTIONS = [
  "FM Technician",
  "Cleaning Supervisor",
  "Cleaning Crew",
  "Day Porter",
  "Pest Control Technician",
  "Facade / Rope Access",
  "Site Supervisor",
  "Account Manager / Sales",
  "Operations Coordinator",
  "Finance / Admin",
  "Other",
];

const VISA_OPTIONS = [
  "Cancelled / on cancellation",
  "Visit visa",
  "Employment visa (transferable)",
  "Employment visa (non-transferable)",
  "UAE national / GCC",
  "Outside UAE",
];

export default function Careers() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role_applied: "",
    years_experience: "",
    nationality: "",
    visa_status: "",
    cv_link: "",
    cover_message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const ACCEPTED_CV_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const MAX_CV_BYTES = 5 * 1024 * 1024;

  const onCvChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCvFile(null);
      return;
    }
    if (!ACCEPTED_CV_TYPES.includes(file.type)) {
      toast.error(c.cvTypeError);
      e.target.value = "";
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      toast.error(c.cvSizeError);
      e.target.value = "";
      return;
    }
    setCvFile(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.role_applied || !cvFile) {
      toast.error(c.validation);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("language", lang);
      fd.append("cv_file", cvFile);
      await submitCareer(fd);
      setSuccess(true);
      toast.success(c.successTitle, { description: c.successDesc });
    } catch (err) {
      const apiMsg = err?.response?.data?.detail;
      toast.error(typeof apiMsg === "string" ? apiMsg : c.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <SEO
        title={`Careers at Orion FM — UAE Hiring | ${COMPANY.brand}`}
        description="Join Orion FM — we're hiring FM technicians, cleaning crew, supervisors and account managers across Dubai, Abu Dhabi, Sharjah and Ajman. Visa sponsored. Apply now."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Orion FM team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="container-orion relative py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              {c.eyebrow}
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {c.h1}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{c.intro}</p>
            <a
              href="#apply"
              data-testid="careers-cta-apply"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
            >
              {c.formTitle}
              <ArrowRight className="w-4 h-4 rtl-flip" />
            </a>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">{c.perksTitle}</div>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.perks.map((p, i) => {
              const Icon = p.Icon;
              return (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-7 card-hover">
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{p.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-16 bg-slate-50">
        <div className="container-orion">
          <div className="text-eyebrow text-blue-700">{c.rolesTitle}</div>
          <div className="mt-6 flex flex-wrap gap-2">
            {c.roles.map((r, i) => (
              <span
                key={i}
                data-testid={`careers-role-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700"
              >
                <Briefcase className="w-3.5 h-3.5 text-blue-700" />
                {r}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Apply */}
      <section id="apply" className="py-20 lg:py-24 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-eyebrow text-blue-700">{c.formTitle}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              {c.formTitle}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{c.formNote}</p>
            <div className="mt-6 text-sm text-slate-500">
              Direct: <a href="mailto:careers@orionfm.ae" className="text-blue-700 underline">careers@orionfm.ae</a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              {success ? (
                <div data-testid="careers-success" className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="mt-5 font-heading text-2xl font-medium text-slate-900">{c.successTitle}</h3>
                  <p className="mt-3 text-slate-600">{c.successDesc}</p>
                </div>
              ) : (
                <form data-testid="careers-form" onSubmit={onSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.name}</Label>
                      <Input
                        data-testid="careers-form-name"
                        value={form.name}
                        onChange={update("name")}
                        required
                        className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.phone}</Label>
                      <Input
                        data-testid="careers-form-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        required
                        placeholder="+971 ..."
                        className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.email}</Label>
                    <Input
                      data-testid="careers-form-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.role}</Label>
                      <Select value={form.role_applied} onValueChange={set("role_applied")}>
                        <SelectTrigger
                          data-testid="careers-form-role"
                          className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
                        >
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.experience}</Label>
                      <Input
                        data-testid="careers-form-experience"
                        value={form.years_experience}
                        onChange={update("years_experience")}
                        placeholder="e.g. 3 years"
                        className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.nationality}</Label>
                      <Input
                        data-testid="careers-form-nationality"
                        value={form.nationality}
                        onChange={update("nationality")}
                        className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.visa}</Label>
                      <Select value={form.visa_status} onValueChange={set("visa_status")}>
                        <SelectTrigger
                          data-testid="careers-form-visa"
                          className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
                        >
                          <SelectValue placeholder="Select visa status" />
                        </SelectTrigger>
                        <SelectContent>
                          {VISA_OPTIONS.map((v) => (
                            <SelectItem key={v} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.cv}</Label>
                    <Input
                      data-testid="careers-form-cv-file"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={onCvChange}
                      className="h-12 rounded-lg border-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:px-3 file:py-2 file:text-xs file:font-semibold hover:file:bg-blue-100 cursor-pointer focus-visible:ring-blue-500"
                    />
                    <p className="text-[11px] text-slate-400">
                      {cvFile ? `✓ ${cvFile.name} (${(cvFile.size / 1024).toFixed(0)} KB)` : c.fields.cvHint}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.cvLink}</Label>
                    <Input
                      data-testid="careers-form-cv"
                      value={form.cv_link}
                      onChange={update("cv_link")}
                      placeholder="https://..."
                      className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">{c.fields.cover}</Label>
                    <Textarea
                      data-testid="careers-form-cover"
                      value={form.cover_message}
                      onChange={update("cover_message")}
                      rows={3}
                      className="rounded-lg border-slate-300 focus-visible:ring-blue-500"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="careers-form-submit"
                    disabled={submitting}
                    className="btn-primary-orion w-full disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {c.fields.sending}
                      </>
                    ) : (
                      <>
                        {c.fields.submit}
                        <ArrowRight className="w-4 h-4 rtl-flip" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
